import React, { useState, useEffect, useRef } from 'react'
import { BarChart3, FileText, Zap, Clock, Link2 } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useSubscription } from '@/hooks/useSubscription'
import type { User } from '@supabase/supabase-js'

interface UsageCardProps {
  isCollapsed?: boolean
  hasActiveSubscription?: boolean
  user?: User | null
}

interface UsageData {
  articlesUsed: number
  articlesLimit: number
  analysesUsed: number
  analysesLimit: number
  backlinksUsed: number
  backlinksLimit: number
  resetDate: string | null
  daysUntilReset: number | null
}

export function UsageCard({ isCollapsed = false, hasActiveSubscription = false, user }: UsageCardProps) {
  const { subscription, benefits } = useSubscription(user || null)
  const [usageData, setUsageData] = useState<UsageData>({
    articlesUsed: 0,
    articlesLimit: 30,
    analysesUsed: 0,
    analysesLimit: 1,
    backlinksUsed: 0,
    backlinksLimit: 10,
    resetDate: null,
    daysUntilReset: null
  })
  const [loading, setLoading] = useState(false) // Start with false - only show loading on first load
  const hasLoadedOnceRef = useRef(false) // Track if we've loaded data at least once (using ref to avoid re-renders)

  useEffect(() => {
    if (!user || !hasActiveSubscription) {
      setLoading(false)
      return
    }

    const loadUsageData = async () => {
      try {
        // Only show loading spinner on the very first load
        // On subsequent loads (e.g., when subscription/benefits update), refresh silently
        const isFirstLoad = !hasLoadedOnceRef.current
        if (isFirstLoad) {
          setLoading(true)
        }

        // Get subscription benefits - these are the source of truth for usage
        // Always fetch fresh data from database to ensure accuracy
        let currentBenefits = benefits
        if (subscription?.id) {
          const { data: benefitsData, error: benefitsError } = await supabase
            .from('subscription_benefits')
            .select('*')
            .eq('subscription_id', subscription.id)
            .order('benefit_type', { ascending: true })
          
          if (!benefitsError && benefitsData && benefitsData.length > 0) {
            currentBenefits = benefitsData
          } else if (benefitsError) {
            console.warn('⚠️ Error fetching fresh benefits, using cached:', benefitsError)
          }
        }

        const articleBenefit = currentBenefits.find(b => b.benefit_type === 'article_generation')
        const analysisBenefit = currentBenefits.find(b => b.benefit_type === 'analysis')
        const backlinkBenefit = currentBenefits.find(b => b.benefit_type === 'backlinks')

        // Calculate reset date from subscription renewal - ONLY if subscription is active/trialing
        let resetDate: string | null = null
        let daysUntilReset: number | null = null
        
        // Only show reset date if subscription is actually active and paid
        if (subscription && (subscription.status === 'active' || subscription.status === 'trialing')) {
          if (subscription.current_period_end) {
            resetDate = subscription.current_period_end
            const reset = new Date(subscription.current_period_end)
            const today = new Date()
            const diffTime = reset.getTime() - today.getTime()
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            daysUntilReset = Math.max(0, diffDays)
          }
        } else {
          // Subscription not active - no reset date
          resetDate = null
          daysUntilReset = null
        }

        // Use used_count from subscription_benefits as the source of truth
        // This ensures accuracy and prevents glitches from database counting
        setUsageData({
          articlesUsed: articleBenefit?.used_count || 0,
          articlesLimit: articleBenefit?.benefit_limit || 30,
          analysesUsed: analysisBenefit?.used_count || 0,
          analysesLimit: analysisBenefit?.benefit_limit || 1,
          backlinksUsed: backlinkBenefit?.used_count || 0,
          backlinksLimit: backlinkBenefit?.benefit_limit || 10,
          resetDate,
          daysUntilReset
        })
        hasLoadedOnceRef.current = true // Mark that we've loaded data at least once
      } catch (error) {
        console.error('Error loading usage data:', error)
        hasLoadedOnceRef.current = true // Mark as loaded even on error to prevent infinite loading
      } finally {
        setLoading(false)
      }
    }

    loadUsageData()
    
    // Set up polling as fallback if real-time doesn't work
    // Poll every 5 seconds when generating articles
    const pollInterval = setInterval(() => {
      if (hasActiveSubscription && subscription?.id) {
        // Silently refresh benefits
        loadUsageData()
      }
    }, 5000) // Poll every 5 seconds
    
    return () => {
      clearInterval(pollInterval)
    }
  }, [user, hasActiveSubscription, subscription, benefits])

  if (isCollapsed) {
    return (
      <div className="usage-card rounded-xl bg-sidebar-accent/50 border border-sidebar-border/50 p-3 mb-4">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </div>
    )
  }

  if (!hasActiveSubscription) {
    // Show different message based on subscription status
    let statusMessage = 'Subscribe to track usage';
    let statusColor = 'text-muted-foreground';
    
    if (subscription) {
      switch (subscription.status) {
        case 'expired':
        case 'canceled':
          statusMessage = 'Subscription expired';
          statusColor = 'text-orange-600';
          break;
        case 'past_due':
        case 'unpaid':
          statusMessage = 'Payment required';
          statusColor = 'text-orange-600';
          break;
        case 'incomplete':
        case 'incomplete_expired':
          statusMessage = 'Payment incomplete';
          statusColor = 'text-orange-600';
          break;
        default:
          statusMessage = 'Subscription inactive';
          statusColor = 'text-muted-foreground';
      }
    }
    
    return (
      <div className="usage-card rounded-xl bg-sidebar-accent/50 border border-sidebar-border/50 p-2.5 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 rounded bg-muted/50 flex items-center justify-center">
            <BarChart3 className="w-3 h-3 text-muted-foreground" />
          </div>
          <span className="font-semibold text-sidebar-foreground text-xs tracking-tight">Usage</span>
        </div>
        <div className={`text-xs ${statusColor} text-center py-2`}>
          {statusMessage}
        </div>
        {subscription && (subscription.status === 'expired' || subscription.status === 'past_due' || subscription.status === 'unpaid') && (
          <div className="text-xs text-muted-foreground text-center pb-1">
            Renew to reset limits
          </div>
        )}
      </div>
    )
  }

  const articlesPercent = usageData.articlesLimit > 0 
    ? Math.min(100, (usageData.articlesUsed / usageData.articlesLimit) * 100)
    : 0
  const analysesPercent = usageData.analysesLimit > 0
    ? Math.min(100, (usageData.analysesUsed / usageData.analysesLimit) * 100)
    : 0
  const backlinksPercent = usageData.backlinksLimit > 0
    ? Math.min(100, (usageData.backlinksUsed / usageData.backlinksLimit) * 100)
    : 0

  return (
    <div className="usage-card rounded-xl bg-sidebar-accent/50 border border-sidebar-border/50 p-2.5 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 rounded bg-muted/50 flex items-center justify-center">
          <BarChart3 className="w-3 h-3 text-muted-foreground" />
        </div>
        <span className="font-semibold text-sidebar-foreground text-xs tracking-tight">Usage</span>
      </div>
      
      <div className="space-y-2">
        {/* Articles Usage */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <FileText className="w-3 h-3 text-primary/60" />
              <span className="text-xs text-muted-foreground">Articles</span>
            </div>
            <span className="text-xs font-medium text-sidebar-foreground">
              {loading ? '...' : `${usageData.articlesUsed}/${usageData.articlesLimit}`}
            </span>
          </div>
          <div className="w-full bg-muted/50 rounded-full h-1 overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500" 
              style={{ width: `${articlesPercent}%` }}
            ></div>
          </div>
        </div>
        
        {/* Analyses Usage */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-primary/60" />
              <span className="text-xs text-muted-foreground">Analyses</span>
            </div>
            <span className="text-xs font-medium text-sidebar-foreground">
              {loading ? '...' : `${usageData.analysesUsed}/${usageData.analysesLimit}`}
            </span>
          </div>
          <div className="w-full bg-muted/50 rounded-full h-1 overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500" 
              style={{ width: `${analysesPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Backlinks Usage */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Link2 className="w-3 h-3 text-primary/60" />
              <span className="text-xs text-muted-foreground">Backlinks</span>
            </div>
            <span className="text-xs font-medium text-sidebar-foreground">
              {loading ? '...' : `${usageData.backlinksUsed}/${usageData.backlinksLimit}`}
            </span>
          </div>
          <div className="w-full bg-muted/50 rounded-full h-1 overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500" 
              style={{ width: `${backlinksPercent}%` }}
            ></div>
          </div>
        </div>
        
        {/* Reset Timer - Only show if subscription is active and has reset date */}
        {subscription && (subscription.status === 'active' || subscription.status === 'trialing') && usageData.daysUntilReset !== null && (
          <div className="flex items-center justify-between pt-1.5 border-t border-sidebar-border/30">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-primary/60" />
              <span className="text-xs text-muted-foreground">Resets in</span>
            </div>
            <span className="text-xs font-medium text-sidebar-foreground">
              {usageData.daysUntilReset === 0 
                ? 'Today' 
                : usageData.daysUntilReset === 1 
                ? '1 day'
                : `${usageData.daysUntilReset} days`
              }
            </span>
          </div>
        )}
        
        {/* Show warning if subscription is not active */}
        {subscription && subscription.status !== 'active' && subscription.status !== 'trialing' && (
          <div className="flex items-center justify-between pt-1.5 border-t border-sidebar-border/30">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-orange-500" />
              <span className="text-xs text-orange-600 font-medium">Subscription {subscription.status}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
